'use client';
import { useMemo } from 'react';
import { GameSection } from '@/widgets/game-section';
import { useI18n } from '@locales/client';
import { useConfirmDataPrompt } from '@/entities/data-confirmation/model/use-confirm-data-prompt';
import { useUserId } from '@/shared/providers/user-provider/use-user';
import { EMPTY_ARRAY } from '@/widgets/game-sections/ui/empty-values';

const RenderCategories = ({
  isGames = false,
  isNotFound = false,
  openNotAllowedModal,
  setCurrentGame,
  categoriesWithGames = EMPTY_ARRAY,
  recommendedGame = EMPTY_ARRAY,
  preferredGame = EMPTY_ARRAY,
  newGame = EMPTY_ARRAY,
  totalGamesCount,
  activeBonusId,
}) => {
  const { role } = useConfirmDataPrompt();
  const t = useI18n();
  const userId = useUserId();

  // Фильтрация категорий с учетом авторизации
  const { filteredCategories, bonusCategory } = useMemo(() => {
    const filtered: Array<(typeof categoriesWithGames)[number]> = [];
    let bonus;

    for (const category of categoriesWithGames ?? []) {
      if (category.id === 'bonus') {
        if (!userId) continue;
        bonus ??= category;
      }
      filtered.push(category);
    }

    return { filteredCategories: filtered, bonusCategory: bonus };
  }, [categoriesWithGames, userId]);

  const renderGameSection = (title = '', games = [], viewCount = true) => (
    <GameSection
      key={games?.at(-1)?.id + title}
      isGames={isGames}
      hasScroll={true}
      rows={games?.length < 4 ? 1 : 2}
      title={title}
      openNotAllowedModal={openNotAllowedModal}
      games={games}
      onGameClick={setCurrentGame}
      viewCount={viewCount}
      totalGamesCount={totalGamesCount}
      activeBonusId={activeBonusId}
    />
  );

  // Обработка случая isNotFound
  if (isNotFound) {
    return (
      <>
        {renderGameSection('new', newGame, true)}
        {renderGameSection(t('favorite_games'), preferredGame, false)}
      </>
    );
  }

  const renderPopular = ({ filter = false, recommended = false }) => {
    const isBonusActive = bonusCategory?.games?.length;

    if (recommended && preferredGame?.length < 2) {
      filter = true;
      recommended = false;
    }

    const renderBonusSection = () => {
      return isBonusActive
        ? renderGameSection(bonusCategory.id, bonusCategory.games, true)
        : null;
    };

    const renderRecommendedSections = () => [
      renderBonusSection(),
      renderGameSection(
        t('favorite_games'),
        preferredGame,
        isBonusActive ? false : true
      ),
      renderGameSection(t('recommended_games'), recommendedGame, false),
    ];

    const renderPopularSections = () =>
      filteredCategories
        .filter((category) =>
          filter ? category.id === 'popular' || category.id === 'bonus' : true
        )
        .map((section) => (
          <GameSection
            key={section.id}
            isGames={isGames}
            hasScroll={true}
            rows={section?.gamesCount < 4 ? 1 : 2}
            title={section?.id}
            openNotAllowedModal={openNotAllowedModal}
            games={section.games}
            onGameClick={setCurrentGame}
            viewCount={section.games.length > 6}
            totalGamesCount={totalGamesCount}
            activeBonusId={activeBonusId}
          />
        ));

    return recommended ? renderRecommendedSections() : renderPopularSections();
  };

  if (isGames) return renderPopular({});

  return renderPopular(
    role !== 'visitor' ? { recommended: true } : { filter: true }
  );
};

export { RenderCategories };
